import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { PricesheetService } from "@services/finance/pricesheets/pricesheetService";
import { JourneyService } from "@services/journey/journeyService";
import { ContractService } from "@services/finance/contracts/contractService";
import { Contract } from "@models/contract.model";
import { Pricesheet } from "@models/pricesheet.model";
import { PricesheetItem } from "@models/pricesheetitem.model";
import { JourneyPath } from "@models/journeyPath.model";
import { CommandGetPopulationSegments } from "@services/organizations/commands/cmdGetPopulationSegments";
import type { PopulationSegment } from "@models/populationSegment.model";
import CurrencyInput from "../components/pricesheet/CurrencyInput";
import PathsAccordion, { duplicateCellIds } from "../components/pricesheet/PathsAccordion";
import { LineItemRow } from "../components/pricesheet/PriceSheetLineItem";
import { JourneyStepOption } from "../components/pricesheet/JourneyStepSelect";
import JourneyPathNameModal, { takenPopulations } from "../components/modals/JourneyPathNameModal";
import RemoveJourneyPathModal from "../components/modals/RemoveJourneyPathModal";
import { PathDraft } from "../components/modals/PricesheetFormModal";
import showAlert from "src/shared/utils/generic.tsx";
import { Paths } from "src/paths";
import { readReturnTo } from "src/shared/utils/returnTo";

// ----- helpers (mirrored from PricesheetFormModal) -----

const JOURNEY_ID = 4;

function toItemDraft(item: PricesheetItem): LineItemRow {
  return {
    id: item.id,
    name: item.name,
    journey_step_id: item.journey_step_id,
    journey_path_id: item.journey_path_id,
    step_price: item.step_price,
    estimated_roi: item.estimated_roi ?? 0,
    unit_price: item.unit_price,
    sequence: item.sequence ?? 0,
    conversion_rule_text: item.conversion_rule_text ?? "",
    description: item.description ?? "",
    is_mandatory: item.is_mandatory ?? false,
    has_dependency: item.has_dependency ?? false,
    is_supplemental: item.is_supplemental ?? false,
    create_date: item.create_date ?? null,
    journey_step_dimension_id: item.journey_step_dimension_id ?? null,
    journey_step_dimension_name: item.journey_step_dimension_name ?? null,
  };
}

function toPathDraft(p: JourneyPath): PathDraft {
  return { id: p.id, name: p.name, populationSegmentId: p.population_segment_id };
}

function itemsEqual(a: LineItemRow, b: LineItemRow): boolean {
  return (
    a.name === b.name &&
    a.journey_step_id === b.journey_step_id &&
    a.journey_path_id === b.journey_path_id &&
    a.step_price === b.step_price &&
    a.estimated_roi === b.estimated_roi &&
    a.unit_price === b.unit_price &&
    a.sequence === b.sequence &&
    a.conversion_rule_text === b.conversion_rule_text &&
    a.description === b.description &&
    a.is_mandatory === b.is_mandatory &&
    a.has_dependency === b.has_dependency &&
    a.journey_step_dimension_id === b.journey_step_dimension_id
  );
}

function flattenSteps(nodes: any[], depth = 0): JourneyStepOption[] {
  const result: JourneyStepOption[] = [];
  for (const node of nodes) {
    const hasChildren = !!node.children?.length;
    result.push({ id: node.id, name: node.name, depth, hasChildren });
    if (hasChildren) {
      result.push(...flattenSteps(node.children, depth + 1));
    }
  }
  return result;
}

function toItemPayload(row: LineItemRow, pathIdMap: Map<number, number>) {
  let pathId = row.journey_path_id;
  if (pathId !== null && pathId < 0) {
    const resolved = pathIdMap.get(pathId);
    pathId = resolved ?? null;
  }
  return {
    name: row.name,
    journey_step_id: row.journey_step_id,
    journey_path_id: pathId,
    step_price: row.step_price,
    estimated_roi: row.estimated_roi,
    unit_price: row.unit_price,
    sequence: row.sequence,
    conversion_rule_text: row.conversion_rule_text,
    description: row.description ?? "",
    is_mandatory: row.is_mandatory ?? false,
    has_dependency: row.has_dependency ?? false,
    journey_step_dimension_id: row.journey_step_dimension_id ?? null,
  };
}

interface NameModalState {
  open: boolean;
  mode: "add" | "rename";
  pathId?: number;
  initialName?: string;
  initialSegmentId?: number | null;
}

const CLOSED_NAME_MODAL: NameModalState = { open: false, mode: "add" };

const pricesheetService = new PricesheetService();
const contractService = new ContractService();
const journeyService = new JourneyService();

// ----- page component -----

const ContractPricesheetPage: FC = () => {
  const { contractId: contractIdParam, pricesheetId: pricesheetIdParam } = useParams<{
    contractId: string;
    pricesheetId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const contractId = contractIdParam ? parseInt(contractIdParam, 10) : null;
  const pricesheetId = pricesheetIdParam ? parseInt(pricesheetIdParam, 10) : null;

  // Remote data
  const [contract, setContract] = useState<Contract | null>(null);
  const [pricesheet, setPricesheet] = useState<Pricesheet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [contractGroups, setContractGroups] = useState<JourneyPath[]>([]);

  // Editor state (mirrors PricesheetFormModal)
  const [basePrice, setBasePrice] = useState("0");
  const [isActive, setIsActive] = useState(true);
  const [drafts, setDrafts] = useState<LineItemRow[]>([]);
  const [paths, setPaths] = useState<PathDraft[]>([]);
  const [stepOptions, setStepOptions] = useState<JourneyStepOption[]>([]);
  const [segments, setSegments] = useState<PopulationSegment[]>([]);
  const [nameModal, setNameModal] = useState<NameModalState>(CLOSED_NAME_MODAL);
  const [removeConfirm, setRemoveConfirm] = useState<{
    pathId: number | null;
    name: string;
    itemCount: number;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const originalItemById = useRef<Map<number, LineItemRow>>(new Map());
  const originalPathById = useRef<Map<number, PathDraft>>(new Map());
  const nextItemIdRef = useRef(-1);
  const nextPathIdRef = useRef(-1);

  // Load contract + pricesheet + items + paths + journey steps
  useEffect(() => {
    if (contractId == null || pricesheetId == null) return;

    setIsLoading(true);
    setLoadError(null);

    Promise.all([
      contractService.get(contractId),
      pricesheetService.get(contractId, pricesheetId),
      pricesheetService.listItems(contractId, pricesheetId),
      pricesheetService.listPaths(contractId, pricesheetId),
    ])
      .then(([contractData, psData, items, pathsData]) => {
        if (!psData) {
          setLoadError("Pricesheet not found.");
          return;
        }
        setContract(contractData);
        setPricesheet(psData);

        const sorted = [...items]
          .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
          .map(toItemDraft);
        setDrafts(sorted);
        originalItemById.current = new Map(sorted.map((d) => [d.id, d]));
        nextItemIdRef.current = -1;

        setBasePrice(String(psData.base_price ?? 0));
        setIsActive(psData.is_active);

        const draftedPaths = pathsData.map(toPathDraft);
        setPaths(draftedPaths);
        originalPathById.current = new Map(draftedPaths.map((p) => [p.id, p]));
        nextPathIdRef.current = -1;

        // Load journey steps
        return journeyService.getJourneySteps(contractData?.journey_id ?? JOURNEY_ID);
      })
      .then((tree) => {
        if (tree) setStepOptions(flattenSteps(tree));
      })
      .catch(() => {
        setLoadError("Failed to load pricesheet.");
      })
      .finally(() => setIsLoading(false));
  }, [contractId, pricesheetId]);

  // The client org's populations, for each group's scope picker (MPN-1471).
  useEffect(() => {
    const orgId = contract?.client_org_id;
    if (!orgId) return;
    new CommandGetPopulationSegments()
      .run(orgId, 500, 0)
      .then(setSegments)
      .catch(() => setSegments([]));
  }, [contract?.client_org_id]);

  // Every live group on the contract, so the group picker can grey out
  // populations a group on ANOTHER sheet already prices (MPN-1713).
  useEffect(() => {
    if (contractId == null) return;
    pricesheetService.listContractPaths(contractId).then(setContractGroups);
  }, [contractId]);

  // ----- item handlers -----

  const handleAddItem = (pathId: number | null) => {
    const id = nextItemIdRef.current--;
    setDrafts((prev) => [
      ...prev,
      {
        id,
        name: "",
        journey_step_id: 0,
        journey_path_id: pathId,
        step_price: 0,
        estimated_roi: 0,
        unit_price: 0,
        // One past the highest sequence already in this group. The old
        // zero-based count made the first item in every group sequence 0,
        // which used to mean "never bills" (MPN-1589).
        sequence:
          Math.max(
            0,
            ...prev
              .filter((d) => (d.journey_path_id ?? null) === pathId)
              .map((d) => d.sequence ?? 0),
          ) + 1,
        conversion_rule_text: "",
        description: "",
        is_mandatory: false,
        has_dependency: false,
        is_supplemental: false,
        create_date: null,
        journey_step_dimension_id: null,
        journey_step_dimension_name: null,
      },
    ]);
  };

  const handleDimChange = (
    itemId: number,
    journeyStepDimensionId: number | null,
    journeyStepDimensionName: string | null,
  ) => {
    handleChangeItem(itemId, {
      journey_step_dimension_id: journeyStepDimensionId,
      journey_step_dimension_name: journeyStepDimensionName,
    });
  };

  const handleChangeItem = (id: number, patch: Partial<LineItemRow>) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  };

  const handleDeleteItem = (id: number) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  };

  // ----- path handlers -----

  const handleRequestAddPath = () => {
    setNameModal({ open: true, mode: "add" });
  };

  const handleRequestRenamePath = (pathId: number | null) => {
    if (pathId === null) {
      setNameModal({ open: true, mode: "rename" });
      return;
    }
    const current = paths.find((p) => p.id === pathId);
    setNameModal({
      open: true,
      mode: "rename",
      pathId,
      initialName: current?.name ?? "",
      initialSegmentId: current?.populationSegmentId ?? null,
    });
  };

  const handleSubmitName = (name: string, populationSegmentId: number | null) => {
    if (nameModal.mode === "add") {
      const id = nextPathIdRef.current--;
      setPaths((prev) => [...prev, { id, name, populationSegmentId }]);
    } else if (nameModal.mode === "rename" && nameModal.pathId !== undefined) {
      const targetId = nameModal.pathId;
      setPaths((prev) => prev.map((p) => (p.id === targetId ? { ...p, name, populationSegmentId } : p)));
    } else if (nameModal.mode === "rename" && nameModal.pathId === undefined) {
      const id = nextPathIdRef.current--;
      setPaths((prev) => [...prev, { id, name, populationSegmentId }]);
      setDrafts((prev) => prev.map((d) => (d.journey_path_id ? d : { ...d, journey_path_id: id })));
    }
    setNameModal(CLOSED_NAME_MODAL);
  };

  const removePath = (pathId: number | null) => {
    if (pathId === null) {
      setDrafts((prev) => prev.filter((d) => d.journey_path_id));
      return;
    }
    setPaths((prev) => prev.filter((p) => p.id !== pathId));
    setDrafts((prev) => prev.filter((d) => d.journey_path_id !== pathId));
  };

  const handleDeletePath = (pathId: number | null) => {
    if (pathId === null) {
      const itemCount = drafts.filter((d) => !d.journey_path_id).length;
      if (itemCount === 0) return;
      setRemoveConfirm({ pathId: null, name: "Unassigned", itemCount });
      return;
    }
    const target = paths.find((p) => p.id === pathId);
    if (!target) return;
    const itemCount = drafts.filter((d) => d.journey_path_id === pathId).length;
    if (itemCount === 0) {
      removePath(pathId);
      return;
    }
    setRemoveConfirm({ pathId, name: target.name, itemCount });
  };

  // Mirrors the diff computed in handleSave so the Save button can flag unsaved
  // work. Reordering counts because itemsEqual compares `sequence`.
  const hasUnsavedChanges = useMemo(() => {
    if (!pricesheet) return false;
    if (parseFloat(basePrice || "0") !== (pricesheet.base_price ?? 0)) return true;
    if (isActive !== pricesheet.is_active) return true;

    const currentPathIds = new Set(paths.filter((p) => p.id > 0).map((p) => p.id));
    for (const p of paths) {
      if (p.id < 0) return true;
      const orig = originalPathById.current.get(p.id);
      if (orig && (orig.name !== p.name || orig.populationSegmentId !== p.populationSegmentId)) return true;
    }
    for (const id of originalPathById.current.keys()) {
      if (!currentPathIds.has(id)) return true;
    }

    const currentItemIds = new Set(drafts.filter((d) => d.id > 0).map((d) => d.id));
    for (const d of drafts) {
      if (d.id < 0) return true;
      const orig = originalItemById.current.get(d.id);
      if (orig && !itemsEqual(d, orig)) return true;
    }
    for (const id of originalItemById.current.keys()) {
      if (!currentItemIds.has(id)) return true;
    }
    return false;
  }, [drafts, paths, basePrice, isActive, pricesheet]);

  const takenSegments = useMemo(
    () => takenPopulations(contractGroups, paths, pricesheetId, nameModal.pathId),
    [contractGroups, paths, pricesheetId, nameModal.pathId],
  );

  const duplicateItemIds = useMemo(() => duplicateCellIds(drafts), [drafts]);

  // ----- save (same diffing logic as PricesheetFormModal + useOrganizationContracts.savePricesheet) -----

  const handleSave = async () => {
    if (contractId == null || pricesheetId == null) return;
    setIsSaving(true);
    // Each write is committed to the baseline (and to the drafts, for rows that
    // acquire a real id) the moment it succeeds. A failure mid-batch then leaves
    // exactly the unsaved work as the diff: the operator keeps their edits, and
    // a retry cannot create the rows already written a second time.
    const pathIdMap = new Map<number, number>();
    const realPathId = (id: number | null) => (id !== null && pathIdMap.get(id)) || id;
    try {
      // Pricesheet header
      await pricesheetService.update(contractId, pricesheetId, {
        base_price: basePrice ? parseFloat(basePrice) : 0,
        is_active: isActive,
      });

      // Path changes: delete (cascades to items on BE), then rename, then create
      const newPaths = paths.filter((p) => p.id < 0);
      const editedPaths = paths.filter((p) => {
        if (p.id < 0) return false;
        const orig = originalPathById.current.get(p.id);
        return !!orig && (orig.name !== p.name || orig.populationSegmentId !== p.populationSegmentId);
      });
      const currentPathIds = new Set(paths.filter((p) => p.id > 0).map((p) => p.id));
      const deletedPathIds = Array.from(originalPathById.current.keys()).filter(
        (id) => !currentPathIds.has(id),
      );

      for (const id of deletedPathIds) {
        await pricesheetService.deletePath(contractId, pricesheetId, id);
        originalPathById.current.delete(id);
        for (const [itemId, orig] of originalItemById.current) {
          if (orig.journey_path_id === id) originalItemById.current.delete(itemId);
        }
      }
      for (const p of editedPaths) {
        await pricesheetService.updatePath(contractId, pricesheetId, p.id, {
          name: p.name,
          population_segment_id: p.populationSegmentId,
        });
        originalPathById.current.set(p.id, p);
      }
      for (const p of newPaths) {
        const created = await pricesheetService.createPath(
          contractId, pricesheetId, p.name, p.populationSegmentId,
        );
        pathIdMap.set(p.id, created.id);
        originalPathById.current.set(created.id, { ...p, id: created.id });
        setPaths((prev) => prev.map((x) => (x.id === p.id ? { ...x, id: created.id } : x)));
        setDrafts((prev) =>
          prev.map((d) => (d.journey_path_id === p.id ? { ...d, journey_path_id: created.id } : d)),
        );
      }

      // Item changes. Items of a deleted path left the baseline with it above.
      const newItems = drafts.filter((d) => d.id < 0);
      const editedItems = drafts.filter((d) => {
        if (d.id < 0) return false;
        const orig = originalItemById.current.get(d.id);
        return !!orig && !itemsEqual(d, orig);
      });
      const currentItemIds = new Set(drafts.filter((d) => d.id > 0).map((d) => d.id));
      const deletedIds = Array.from(originalItemById.current.keys()).filter(
        (id) => !currentItemIds.has(id),
      );

      for (const id of deletedIds) {
        await pricesheetService.deleteItem(contractId, pricesheetId, id);
        originalItemById.current.delete(id);
      }
      for (const item of editedItems) {
        await pricesheetService.updateItem(
          contractId,
          pricesheetId,
          item.id,
          toItemPayload(item, pathIdMap),
        );
        originalItemById.current.set(item.id, { ...item, journey_path_id: realPathId(item.journey_path_id) });
      }
      for (const item of newItems) {
        const created = await pricesheetService.createItem(
          contractId,
          pricesheetId,
          toItemPayload(item, pathIdMap),
        );
        originalItemById.current.set(created.id, {
          ...item,
          id: created.id,
          journey_path_id: realPathId(item.journey_path_id),
        });
        setDrafts((prev) =>
          prev.map((d) =>
            d.id === item.id ? { ...d, id: created.id, journey_path_id: realPathId(d.journey_path_id) } : d,
          ),
        );
      }

      showAlert("Pricesheet saved", "success", 3000);

      // Reload to reset diff baseline
      const [freshItems, freshPaths, freshPs] = await Promise.all([
        pricesheetService.listItems(contractId, pricesheetId),
        pricesheetService.listPaths(contractId, pricesheetId),
        pricesheetService.get(contractId, pricesheetId),
      ]);
      if (freshPs) {
        setPricesheet(freshPs);
        setBasePrice(String(freshPs.base_price ?? 0));
        setIsActive(freshPs.is_active);
      }
      const sorted = [...freshItems]
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        .map(toItemDraft);
      setDrafts(sorted);
      originalItemById.current = new Map(sorted.map((d) => [d.id, d]));

      const draftedPaths = freshPaths.map(toPathDraft);
      setPaths(draftedPaths);
      originalPathById.current = new Map(draftedPaths.map((p) => [p.id, p]));
    } catch (e) {
      // The server's 422 names the fix ("already prices step X", "another group
      // already prices this population") — show it, not a generic string.
      showAlert(e instanceof Error && e.message ? e.message : "Failed to save pricesheet", "error", 8000);
    } finally {
      setIsSaving(false);
    }
  };

  // ----- render -----

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </div>
    );
  }

  if (loadError || !pricesheet) {
    return (
      <div className="container-fluid py-4">
        <Alert variant="danger">{loadError ?? "Pricesheet not found."}</Alert>
      </div>
    );
  }

  // Back to the tab that opened this sheet (engagement or organization). The
  // standalone contract page is only for a sheet reached by URL (MPN-1715).
  const backHref =
    readReturnTo(location.search) ??
    (contractId
      ? `${import.meta.env.BASE_URL}${Paths.CONTRACT_DETAIL}/${contractId}`
      : `${import.meta.env.BASE_URL}${Paths.ORGANIZATIONS}`);

  return (
    <>
      <div
        className="d-flex align-items-center gap-2 px-4 py-3 border-bottom bg-white sticky-top"
        style={{ zIndex: 10 }}
      >
        <Button
          variant="link"
          className="p-0 text-decoration-none text-muted"
          onClick={() => navigate(backHref)}
          title="Back to contract"
        >
          <i className="bi bi-arrow-left" />
        </Button>
        <span className="fw-semibold" style={{ fontSize: "1.05rem" }}>
          Pricesheet #{pricesheetId}
          {contract ? ` — Contract #${contractId}` : ""}
        </span>
        <div className="ms-auto d-flex align-items-center gap-3">
          {!isSaving && hasUnsavedChanges && (
            <span
              className="d-flex align-items-center gap-2 small fw-medium"
              style={{ color: "#b7791f" }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#f0ad4e",
                  flexShrink: 0,
                }}
              />
              Unsaved changes
            </span>
          )}
          {duplicateItemIds.size > 0 && (
            <span className="small fw-medium text-danger">
              <i className="bi bi-exclamation-triangle me-1" />
              {duplicateItemIds.size} rows price the same step in one group
            </span>
          )}
          <Button variant="light" onClick={() => navigate(backHref)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || !hasUnsavedChanges || duplicateItemIds.size > 0}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              <>
                <i className={`bi ${hasUnsavedChanges ? "bi-check-circle" : "bi-check2-all"} me-2`} />
                {hasUnsavedChanges ? "Save Changes" : "Saved"}
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="container-fluid py-4" style={{ maxWidth: 1440 }}>
        <Form>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <Form.Check
              type="switch"
              id="ps-page-active-switch"
              label="Active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="fw-semibold mb-0">Base Price</Form.Label>
              <div style={{ width: 160 }}>
                <CurrencyInput
                  flex={1}
                  defaultValue={parseFloat(basePrice) || 0}
                  onCommit={(val) => setBasePrice(String(val))}
                />
              </div>
            </div>
          </div>

          <PathsAccordion
            paths={paths.map((p) => ({
              ...p,
              populationSegmentName:
                segments.find((s) => s.id === p.populationSegmentId)?.name ?? null,
            }))}
            rows={drafts}
            duplicateItemIds={duplicateItemIds}
            stepOptions={stepOptions}
            organizationId={contract?.client_org_id ?? 0}
            contractId={contractId}
            pricesheetId={pricesheetId}
            onAddPath={handleRequestAddPath}
            onRenamePath={handleRequestRenamePath}
            onDeletePath={handleDeletePath}
            onAddItem={handleAddItem}
            onChangeItem={handleChangeItem}
            onDeleteItem={handleDeleteItem}
            onDimChange={handleDimChange}
          />
        </Form>
      </div>

      <JourneyPathNameModal
        show={nameModal.open}
        mode={nameModal.mode}
        initialName={nameModal.initialName}
        initialSegmentId={nameModal.initialSegmentId}
        segments={segments}
        takenSegments={takenSegments}
        onClose={() => setNameModal(CLOSED_NAME_MODAL)}
        onSubmit={handleSubmitName}
      />

      <RemoveJourneyPathModal
        show={removeConfirm !== null}
        pathName={removeConfirm?.name ?? ""}
        itemCount={removeConfirm?.itemCount ?? 0}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={() => {
          if (removeConfirm) removePath(removeConfirm.pathId);
          setRemoveConfirm(null);
        }}
      />
    </>
  );
};

export default ContractPricesheetPage;
